import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'
import { 
  Wallet, 
  PieChart, 
  TrendingUp, 
  Target, 
  BarChart3, 
  Smartphone,
  Globe,
  ShieldCheck,
  Zap,
  ArrowRight,
  Star,
  Sparkles
} from 'lucide-react'
import { useState } from 'react'

interface AppOverviewStepProps {
  onNext: () => void
  onBack: () => void
}

export function AppOverviewStep({ onNext }: AppOverviewStepProps) {
  const { t } = useTranslation()
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null)

  const features = [
    {
      icon: <Wallet className="h-7 w-7 text-white" />,
      title: t('onboarding.appOverview.features.accounts', 'Smart Account Management'),
      description: t('onboarding.appOverview.features.accountsDesc', 'Track multiple accounts with real-time balance updates and visual insights'),
      color: 'bg-gradient-to-br from-blue-500 to-blue-600',
      hoverColor: 'hover:from-blue-600 hover:to-blue-700',
      textColor: 'text-blue-600 dark:text-blue-400'
    },
    {
      icon: <PieChart className="h-7 w-7 text-white" />,
      title: t('onboarding.appOverview.features.transactions', 'Effortless Transaction Tracking'),
      description: t('onboarding.appOverview.features.transactionsDesc', 'Categorize expenses automatically and search through thousands of transactions instantly'),
      color: 'bg-gradient-to-br from-purple-500 to-purple-600',
      hoverColor: 'hover:from-purple-600 hover:to-purple-700',
      textColor: 'text-purple-600 dark:text-purple-400'
    },
    {
      icon: <TrendingUp className="h-7 w-7 text-white" />,
      title: t('onboarding.appOverview.features.budgets', 'Intelligent Budgeting'),
      description: t('onboarding.appOverview.features.budgetsDesc', 'Set budgets that adapt to your spending patterns with smart alerts and recommendations'),
      color: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
      hoverColor: 'hover:from-emerald-600 hover:to-emerald-700',
      textColor: 'text-emerald-600 dark:text-emerald-400'
    },
    {
      icon: <Target className="h-7 w-7 text-white" />,
      title: t('onboarding.appOverview.features.goals', 'Goal Achievement Engine'),
      description: t('onboarding.appOverview.features.goalsDesc', 'Set savings goals and watch your progress with motivational tracking and milestones'),
      color: 'bg-gradient-to-br from-amber-500 to-orange-500',
      hoverColor: 'hover:from-amber-600 hover:to-orange-600',
      textColor: 'text-amber-600 dark:text-amber-400'
    },
    {
      icon: <BarChart3 className="h-7 w-7 text-white" />,
      title: t('onboarding.appOverview.features.analytics', 'Advanced Analytics'),
      description: t('onboarding.appOverview.features.analyticsDesc', 'Discover spending patterns and financial insights with beautiful reports and charts'),
      color: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
      hoverColor: 'hover:from-indigo-600 hover:to-indigo-700',
      textColor: 'text-indigo-600 dark:text-indigo-400'
    },
    {
      icon: <Globe className="h-7 w-7 text-white" />,
      title: t('onboarding.appOverview.features.currency', 'Global Currency Support'),
      description: t('onboarding.appOverview.features.currencyDesc', 'Handle multiple currencies with real-time exchange rates and automatic conversions'),
      color: 'bg-gradient-to-br from-teal-500 to-cyan-500',
      hoverColor: 'hover:from-teal-600 hover:to-cyan-600',
      textColor: 'text-teal-600 dark:text-teal-400'
    }
  ]

  const advantages = [
    {
      icon: <Smartphone className="h-5 w-5 text-secondary dark:text-primary-dark" />,
      title: t('onboarding.appOverview.advantages.crossPlatform', 'Cross-Platform Sync'),
      description: t('onboarding.appOverview.advantages.crossPlatformDesc', 'Access your data anywhere')
    },
    {
      icon: <ShieldCheck className="h-5 w-5 text-secondary dark:text-primary-dark" />,
      title: t('onboarding.appOverview.advantages.security', 'Bank-Grade Security'),
      description: t('onboarding.appOverview.advantages.securityDesc', 'Your data is always protected')
    },
    {
      icon: <Zap className="h-5 w-5 text-secondary dark:text-primary-dark" />,
      title: t('onboarding.appOverview.advantages.performance', 'Lightning Fast'),
      description: t('onboarding.appOverview.advantages.performanceDesc', 'Optimized for speed and efficiency')
    }
  ]

  return (
    <div className="space-y-10">
      {/* Hero Section */}
      <div className="text-center space-y-8">
        <div className="relative mx-auto w-24 h-24 mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary dark:from-primary-dark dark:to-secondary-dark rounded-3xl opacity-20 animate-pulse"></div>
          <div className="relative bg-gradient-to-r from-secondary to-primary dark:from-primary-dark dark:to-secondary-dark rounded-3xl p-5 shadow-xl">
            <Wallet className="h-14 w-14 text-white" />
          </div>
          <div className="absolute -top-3 -right-3">
            <div className="bg-yellow-400 rounded-full p-1.5 shadow-lg animate-bounce">
              <Sparkles className="h-4 w-4 text-yellow-900" />
            </div>
          </div>
        </div>
        
        <div className="space-y-4 max-w-3xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent leading-tight">
            {t('onboarding.appOverview.heroTitle', 'Welcome to Your Financial Future')}
          </h3>
          <p className="text-base text-text-secondary dark:text-text-dark-secondary leading-relaxed">
            {t('onboarding.appOverview.heroDescription', 'Discover how Flows.Cash transforms the way you manage money with powerful features designed for your success.')}
          </p>
        </div>
      </div>

      {/* Features Grid */}
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
            {t('onboarding.appOverview.featuresTitle', 'Powerful Features at Your Fingertips')}
          </h4>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            Everything you need to take control of your finances
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <Card
              key={index}
              className={`group p-6 border border-border dark:border-border-dark bg-background-secondary/50 dark:bg-background-dark-secondary/50 backdrop-blur-xl backdrop-saturate-150 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-[1.02] ${
                hoveredFeature === index ? 'shadow-xl scale-[1.02]' : ''
              }`}
              onMouseEnter={() => setHoveredFeature(index)}
              onMouseLeave={() => setHoveredFeature(null)}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start gap-5">
                <div className={`flex-shrink-0 p-3 rounded-2xl ${feature.color} ${feature.hoverColor} shadow-lg group-hover:shadow-xl transition-all duration-300 ${hoveredFeature === index ? 'scale-110' : ''}`}>
                  {feature.icon}
                </div>
                <div className="space-y-3 flex-1">
                  <h4 className="text-base font-semibold text-text-primary dark:text-text-dark-primary leading-tight">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Key Advantages */}
      <Card className="p-8 bg-gradient-to-br from-secondary/5 to-primary/5 dark:from-primary-dark/5 dark:to-secondary-dark/5 border border-secondary/20 dark:border-primary-dark/20 backdrop-blur-xl backdrop-saturate-150 rounded-2xl shadow-sm">
        <div className="space-y-8">
          <div className="text-center space-y-2">
            <h4 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary flex items-center justify-center gap-3">
              <div className="p-2 rounded-xl bg-secondary/10 dark:bg-primary-dark/10">
                <Zap className="h-5 w-5 text-secondary dark:text-primary-dark" />
              </div>
              {t('onboarding.appOverview.whyChoose', 'Why Choose Flows.Cash?')}
            </h4>
            <p className="text-text-secondary dark:text-text-dark-secondary">
              Built with your success in mind
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="text-center space-y-4 group" style={{ animationDelay: `${index * 150}ms` }}>
                <div className="flex justify-center">
                  <div className="p-4 rounded-2xl bg-secondary/10 dark:bg-primary-dark/10 border border-secondary/20 dark:border-primary-dark/20 group-hover:scale-110 group-hover:bg-secondary/15 dark:group-hover:bg-primary-dark/15 transition-all duration-300">
                    {advantage.icon}
                  </div>
                </div>
                <div className="space-y-2">
                  <h5 className="font-semibold text-text-primary dark:text-text-dark-primary">
                    {advantage.title}
                  </h5>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                    {advantage.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Call to Action */}
      <div className="text-center space-y-8">
        <div className="space-y-3">
          <p className="text-base text-text-primary dark:text-text-dark-primary font-medium">
            {t('onboarding.appOverview.readyMessage', 'Ready to transform your financial life?')}
          </p>
          <p className="text-secondary dark:text-primary-dark font-medium">
            {t('onboarding.appOverview.nextStep', 'Let\'s set up your profile and begin your journey!')}
          </p>
        </div>
        
        <Button 
          onClick={onNext}
          className="bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white px-8 py-3 text-base font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-3 mx-auto h-12 active:scale-[0.98]"
        >
          {t('onboarding.appOverview.letsStart', 'Begin My Journey')}
          <ArrowRight className="w-5 h-5" />
        </Button>
      </div>
    </div>
  )
} 